import { inject, Injectable } from '@angular/core';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { map, Observable } from 'rxjs';
import { AircraftInterface } from 'src/app/ReusableComponent/block-time-report/interfaces/aircraft.interface';
import { TripBlockInfoInterfaces } from 'src/app/ReusableComponent/block-time-report/interfaces/trip-block-info.interfaces';
import { IBlockTimeItem } from 'src/app/ReusableComponent/block-time-report/block-time-report.model';
import { CustomerList } from 'src/app/service/customer.model';
import { LocationService } from 'src/app/service/location.service';
import { MemberService } from 'src/app/service/member.service';
import { ReportService } from 'src/app/service/report.service';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { VendorService } from 'src/app/service/vendor.service';
import { ICommonType } from '@interfaces/common-type.interface';

/**
 * Loads the lists of the block time record modal and saves the record.
 * The service uses the existing endpoints only.
 */
@Injectable({
  providedIn: 'root',
})
export class BlockTimeRecordModalService {
  private readonly reportService = inject(ReportService);
  private readonly serviceRequestService = inject(ServiceRequestService);
  private readonly memberService = inject(MemberService);
  private readonly vendorService = inject(VendorService);
  private readonly locationService = inject(LocationService);

  /** Returns the trip numbers that the user can add a record for. */
  getTrips(): Observable<string[]> {
    return this.serviceRequestService.getTripsBasedOnTripNumber();
  }

  /** Returns the customers as dropdown options. The id is the customer id. */
  getCustomers(): Observable<BaseDropdownInterface[]> {
    return this.memberService
      .getCustomerList()
      .pipe(
        map((customers: CustomerList[]) => (customers ?? []).map((customer) => ({ id: customer.customerID, name: customer.customerName }))),
      );
  }

  /** Returns the air vendors. */
  getAirVendors(): Observable<ICommonType[]> {
    return this.vendorService.getAllAirVendorList();
  }

  /** Returns the airport codes. */
  getAirportCodes(): Observable<string[]> {
    return this.locationService.getAllAirportCodeList().pipe(map((airports) => (airports ?? []).map((airport) => airport.airportCode)));
  }

  /** Returns the service request facts of a trip. */
  getTripBlockInfo(tripId: string): Observable<TripBlockInfoInterfaces> {
    return this.reportService.getTripBlockInfo(tripId);
  }

  /** Returns the aircraft of an air vendor. */
  getAircraftForVendor(vendorId: string): Observable<AircraftInterface[]> {
    return this.reportService.getAircraftsForVendors(vendorId);
  }

  /** Saves a new record (isEdit = false) or changes an existing record (isEdit = true). */
  save(record: IBlockTimeItem, isEdit: boolean): Observable<string> {
    return this.reportService.addBlockTimeRecord(record, isEdit);
  }
}
